import React from "react";
import { Container, Grid, Typography, Box, Card, Chip } from "@mui/material";
import WebIcon from "@mui/icons-material/Web";
import DnsIcon from "@mui/icons-material/Dns";
import StorageIcon from "@mui/icons-material/Storage";
import CloudIcon from "@mui/icons-material/Cloud";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import SecurityIcon from "@mui/icons-material/Security";
import SectionHeading from "./SectionHeading";
import useReveal from "./useReveal";
import { brand } from "../theme";

const domains = [
  {
    icon: <WebIcon />,
    title: "Frontend Architecture",
    stacks: ["React", "Angular", "Next.js", "HTML / CSS", "Bootstrap", "Jinja", "Flutter"],
  },
  {
    icon: <DnsIcon />,
    title: "Backend & Microservices",
    stacks: ["Spring Boot", "Node.js", "Express.js", "Django", "FastAPI"],
  },
  {
    icon: <StorageIcon />,
    title: "Databases & Stores",
    stacks: ["MySQL", "MongoDB", "Oracle", "MS SQL", "PostgreSQL"],
  },
  {
    icon: <CloudIcon />,
    title: "Cloud Infrastructure & DevOps",
    stacks: ["AWS", "Containerization", "CI/CD", "Docker", "Jenkins"],
  },
  {
    icon: <SmartToyIcon />,
    title: "AI Engineering & Deep Tech",
    stacks: ["Generative AI", "Prompt Engineering", "LLM Apps", "RAG Systems", "Vector Storage"],
  },
  {
    icon: <SecurityIcon />,
    title: "Cybersecurity Assurance",
    stacks: ["Security Fundamentals", "Network Security", "OWASP Top 10", "Ethical Hacking"],
  },
];

const TrainingCards = () => {
  const ref = useReveal();
  return (
    <section id="training-section" className="training-section" ref={ref}>
      <Box sx={{ py: { xs: 7, md: 11 }, bgcolor: brand.bgAlt }}>
        <Container>
          <SectionHeading
            eyebrow="TECHNOLOGY CAPABILITY MATRIX"
            title="A Fully Decoupled, Modern Stack"
            subtitle="Cross-functional teams maintaining structural mastery across production-grade technologies — ensuring scalability, cloud-native compliance, and architectural security."
          />
          <Grid container spacing={3} className="reveal-stagger">
            {domains.map((d) => (
              <Grid item xs={12} sm={6} md={4} key={d.title}>
                <Card
                  elevation={0}
                  sx={{
                    height: "100%",
                    p: 3.5,
                    bgcolor: "#fff",
                    border: "1px solid rgba(12,131,200,0.10)",
                    transition: "transform .3s ease, box-shadow .3s ease, border-color .3s ease",
                    "&:hover": {
                      transform: "translateY(-6px)",
                      boxShadow: "0 20px 44px rgba(15,39,64,0.10)",
                      borderColor: brand.blue,
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      mb: 2.5,
                    }}
                  >
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: "12px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: "rgba(12,131,200,0.10)",
                        color: brand.blue,
                      }}
                    >
                      {d.icon}
                    </Box>
                    <Typography variant="h6" sx={{ color: brand.navy, fontWeight: 700, fontSize: "1.05rem" }}>
                      {d.title}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {d.stacks.map((s) => (
                      <Chip
                        key={s}
                        label={s}
                        size="small"
                        sx={{
                          bgcolor: "rgba(252,122,70,0.10)",
                          color: brand.orangeDark,
                          fontWeight: 600,
                          fontSize: "0.78rem",
                        }}
                      />
                    ))}
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
          <Typography
            align="center"
            sx={{ color: brand.slate, mt: 5, fontStyle: "italic", maxWidth: 720, mx: "auto" }}
          >
            Every stack track enforces precise clean-coding standards and modular
            architecture paradigms to meet the compliance standards of international IT
            consultancies.
          </Typography>
        </Container>
      </Box>
    </section>
  );
};

export default TrainingCards;
